import { useEffect, useMemo } from 'react'

import { Filter, QueryOrder } from '@cubejs-client/core'
import { useCubeReportPageFiltersContext } from 'cube'
import {
  DRIFT_CHAT_ACCOUNT_DIMENSIONS,
  DRIFT_CHAT_END_USERS_DIMENSIONS,
  DRIFT_SITE_CONCIERGE_DIMENSIONS,
  DRIFT_SITE_CONCIERGE_MEASURES,
} from 'cube/constants'
import moment from 'moment'

import { ReportOrderType } from '@driftt/api-types'
import { useTableState } from '@driftt/dds'
import { CubeQuery, CubeQueryBuilder, useCubeQuery } from '@driftt/dds.reports'

import { IQueryConfig, ISortConfig } from 'api/report/query/types'
import { SchedulingEventSummaryMetrics } from 'api/report/types'
import { resolveCubeKPIResults } from 'components/KPICard'
import useTimePeriod from 'hooks/useTimePeriod'
import { toEpochMilli } from 'utils/dates'

import { formatPageFilters } from '../MeetingLineChart/useMeetingCount.hook'

interface MeetingRequestData {
  endUserId: number
  endUser: {
    id: string
    email: string
    name: string
    title: string
  }
  companyName: string
  companyIndustry: string
  companyCountry: string
  companyEmployeesRange: string
  createdAt: number
}

const parseId = (id: string) => (id ? parseInt(id) : null)

const formatTableData = (resultSet): MeetingRequestData[] =>
  resultSet?.map((data) => ({
    endUserId: parseInt(data?.[DRIFT_SITE_CONCIERGE_DIMENSIONS.END_USER_ID]),
    endUser: {
      id: `${parseId(data?.[DRIFT_SITE_CONCIERGE_DIMENSIONS.END_USER_ID])}`,
      email: data?.[DRIFT_CHAT_END_USERS_DIMENSIONS.EMAIL],
      name: data?.[DRIFT_CHAT_END_USERS_DIMENSIONS.NAME],
      title: data?.[DRIFT_CHAT_END_USERS_DIMENSIONS.COMPANY_EMPLOYMENT_TITLE],
    },
    companyName: data?.[DRIFT_CHAT_ACCOUNT_DIMENSIONS.COMPANY_NAME],
    companyIndustry: data?.[DRIFT_CHAT_ACCOUNT_DIMENSIONS.INDUSTRY],
    companyCountry: data?.[DRIFT_CHAT_ACCOUNT_DIMENSIONS.COUNTRY],
    companyEmployeesRange:
      data?.[DRIFT_CHAT_ACCOUNT_DIMENSIONS.EMPLOYEE_COUNT_RANGE],
    createdAt: toEpochMilli(
      data?.[DRIFT_SITE_CONCIERGE_DIMENSIONS.EVENT_SENT_AT]
    ),
  }))

const mapSortMetric: Record<string, string> = {
  [SchedulingEventSummaryMetrics.CREATED_AT]:
    DRIFT_SITE_CONCIERGE_DIMENSIONS.EVENT_SENT_AT,
}

const formatSortMetric = (metric: string) => {
  return mapSortMetric[metric]
    ? mapSortMetric[metric]
    : DRIFT_SITE_CONCIERGE_DIMENSIONS.EVENT_SENT_AT
}

const formatSortOrder = (orderType: ReportOrderType) => {
  return orderType.toLowerCase() as QueryOrder
}

export const useMeetingRequests = (
  sortConfig: ISortConfig,
  queryConfig: IQueryConfig,
  extraFilters: Filter[] = []
) => {
  const { tableState, handleTableStateChange } =
    useTableState<MeetingRequestData>({
      defaultPagination: {
        pageIndex: 0,
        pageSize: 10,
      },
    })

  const timePeriod = useTimePeriod()

  const queryFilters = formatPageFilters(queryConfig.compoundQuery)
  const { pageFilters: cubeFilters } = useCubeReportPageFiltersContext()
  const pageFilters = [...queryFilters, ...cubeFilters, ...extraFilters]

  const meetingsBookedBaseCubeQuery: CubeQuery = {
    name: 'DriftSiteConcierge',
    query: {
      order: {
        [DRIFT_SITE_CONCIERGE_DIMENSIONS.EVENT_SENT_AT]: 'desc',
      },
      dimensions: [
        DRIFT_SITE_CONCIERGE_DIMENSIONS.END_USER_ID,
        DRIFT_CHAT_END_USERS_DIMENSIONS.EMAIL,
        DRIFT_CHAT_END_USERS_DIMENSIONS.NAME,
        DRIFT_CHAT_END_USERS_DIMENSIONS.COMPANY_EMPLOYMENT_TITLE,
        DRIFT_CHAT_ACCOUNT_DIMENSIONS.COMPANY_NAME,
        DRIFT_CHAT_ACCOUNT_DIMENSIONS.INDUSTRY,
        DRIFT_CHAT_ACCOUNT_DIMENSIONS.EMPLOYEE_COUNT_RANGE,
        DRIFT_CHAT_ACCOUNT_DIMENSIONS.COUNTRY,
        DRIFT_SITE_CONCIERGE_DIMENSIONS.EVENT_SENT_AT,
      ],
      filters: [
        {
          member: DRIFT_SITE_CONCIERGE_MEASURES.COUNT_UNQUALIFIED_END_USERS,
          operator: 'gt',
          values: ['0'],
        },
      ],
    },
    queryName: 'DriftSiteConcierge',
  }

  const timeDimensions: Array<{
    dimension: string
    dateRange: [string, string]
  }> = [
    {
      dimension: DRIFT_SITE_CONCIERGE_DIMENSIONS.EVENT_SENT_AT,
      dateRange: [
        moment(timePeriod.startDate).format('YYYY-MM-DD'),
        moment(timePeriod.endDate).format('YYYY-MM-DD'),
      ],
    },
  ]

  const paginatedMetricQuery = new CubeQueryBuilder({
    query: meetingsBookedBaseCubeQuery,
  })
    .withName(
      `MeetingsRequests-${tableState.pagination.pageIndex}-${tableState.pagination.pageSize}`
    )
    .withTimeDimensions(timeDimensions)
    .withLimit(tableState.pagination.pageSize)
    .withOffset(
      tableState.pagination.pageIndex * tableState.pagination.pageSize
    )
    .withFilters(pageFilters)
    .withOrder([
      [formatSortMetric(sortConfig.metric), formatSortOrder(sortConfig.order)],
    ])
    .build()

  const {
    data: paginatedResultSet,
    refetch,
    isLoading: isLoadingData,
    isError: isErrorData,
  } = useCubeQuery({
    query: paginatedMetricQuery,
  })

  const paginatedData = useMemo(
    () => formatTableData(paginatedResultSet?.tablePivot()),
    [paginatedResultSet]
  )

  const partialMetric = useMemo(() => paginatedData ?? null, [paginatedData])

  const countQuery = new CubeQueryBuilder({})
    .withTimeDimensions(timeDimensions)
    .withMeasures([DRIFT_SITE_CONCIERGE_MEASURES.COUNT_UNQUALIFIED_END_USERS])
    .withFilters(pageFilters)
    .build()

  const {
    data: countResultSet,
    isLoading: isLoadingCount,
    isError: isErrorCount,
  } = useCubeQuery({
    query: countQuery,
  })

  const totalRowCount = useMemo(
    () =>
      resolveCubeKPIResults(
        countResultSet,
        DRIFT_SITE_CONCIERGE_MEASURES.COUNT_UNQUALIFIED_END_USERS
      ),
    [countResultSet]
  )

  const fullMetricQuery = new CubeQueryBuilder({
    query: meetingsBookedBaseCubeQuery,
  })
    .withTimeDimensions(timeDimensions)
    .withFilters(pageFilters)
    .build()

  const { data: fullResultSet } = useCubeQuery({
    query: fullMetricQuery,
  })

  const fullMetric = useMemo(
    () => formatTableData(fullResultSet?.tablePivot()) || [],
    [fullResultSet]
  )

  const isLoading = useMemo(
    () => isLoadingData || isLoadingCount,
    [isLoadingData, isLoadingCount]
  )

  const isError = useMemo(
    () => isErrorData || isErrorCount,
    [isErrorCount, isErrorData]
  )

  useEffect(() => {
    refetch()
  }, [sortConfig, refetch])

  return useMemo(
    () => ({
      isLoading,
      isError,
      partialMetric,
      totalRowCount,
      fullMetric,
      pagination: tableState.pagination,
      handleTableStateChange,
    }),
    [
      isLoading,
      isError,
      partialMetric,
      fullMetric,
      totalRowCount,
      tableState.pagination,
      handleTableStateChange,
    ]
  )
}

export default useMeetingRequests
